import React from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { LiquidLocalizedVariable } from "../../../utils/liquidUtils";

interface LiquidVariablesModalProps {
    show: boolean;
    onClose: () => void;
    liquidVariables: LiquidLocalizedVariable[] | null;
}

interface FilterVariant {
    syntax: string;
    descriptionKey: string;
    example: string;
}

interface FilterEntry {
    name: string;
    variants: FilterVariant[];
}

interface TagEntry {
    name: string;
    syntax: string;
    descriptionKey: string;
}

const CUSTOM_FILTERS: FilterEntry[] = [
    {
        name: "ifPresent",
        variants: [
            {
                syntax: '{{ var | ifPresent: "suffix" }}',
                descriptionKey: "liquidVariables.reference.filters.ifPresent.variant0",
                example: '{{ guest.comment | ifPresent: "!" }}\n→ "Great event!" or ""',
            },
            {
                syntax: '{{ var | ifPresent: "prefix", "suffix" }}',
                descriptionKey: "liquidVariables.reference.filters.ifPresent.variant1",
                example: '{{ guest.comment | ifPresent: "(", ")" }}\n→ "(Great event!)" or ""',
            },
            {
                syntax: '{{ var | ifPresent: "suffix", nil, "default" }}',
                descriptionKey: "liquidVariables.reference.filters.ifPresent.variant2",
                example: '{{ guest.comment | ifPresent: "", nil, "—" }}\n→ "Great event!" or "—"',
            },
        ],
    },
];

const BUILTIN_FILTERS: FilterEntry[] = [
    {
        name: "default",
        variants: [
            {
                syntax: '{{ var | default: "fallback" }}',
                descriptionKey: "liquidVariables.reference.filters.default",
                example: '{{ guest.comment | default: "No comment" }}',
            },
        ],
    },
    {
        name: "upcase",
        variants: [
            {
                syntax: "{{ var | upcase }}",
                descriptionKey: "liquidVariables.reference.filters.upcase",
                example: "{{ guest.firstName | upcase }}\n→ JOHN",
            },
        ],
    },
    {
        name: "downcase",
        variants: [
            {
                syntax: "{{ var | downcase }}",
                descriptionKey: "liquidVariables.reference.filters.downcase",
                example: "{{ guest.email | downcase }}",
            },
        ],
    },
    {
        name: "capitalize",
        variants: [
            {
                syntax: "{{ var | capitalize }}",
                descriptionKey: "liquidVariables.reference.filters.capitalize",
                example: "{{ guest.firstName | capitalize }}",
            },
        ],
    },
    {
        name: "strip",
        variants: [
            {
                syntax: "{{ var | strip }}",
                descriptionKey: "liquidVariables.reference.filters.strip",
                example: "{{ guest.comment | strip }}",
            },
        ],
    },
    {
        name: "truncate",
        variants: [
            {
                syntax: '{{ var | truncate: 20, "…" }}',
                descriptionKey: "liquidVariables.reference.filters.truncate",
                example: '{{ event.name | truncate: 30, "…" }}',
            },
        ],
    },
    {
        name: "replace",
        variants: [
            {
                syntax: '{{ var | replace: "old", "new" }}',
                descriptionKey: "liquidVariables.reference.filters.replace",
                example: '{{ guest.email | replace: "@", " at " }}',
            },
        ],
    },
    {
        name: "remove",
        variants: [
            {
                syntax: '{{ var | remove: "str" }}',
                descriptionKey: "liquidVariables.reference.filters.remove",
                example: '{{ guest.fullName | remove: "Dr. " }}',
            },
        ],
    },
    {
        name: "prepend",
        variants: [
            {
                syntax: '{{ var | prepend: "prefix" }}',
                descriptionKey: "liquidVariables.reference.filters.prepend",
                example: '{{ guest.lastName | prepend: "Dear " }}',
            },
        ],
    },
    {
        name: "append",
        variants: [
            {
                syntax: '{{ var | append: "suffix" }}',
                descriptionKey: "liquidVariables.reference.filters.append",
                example: '{{ guest.firstName | append: "!" }}',
            },
        ],
    },
];

const TAGS: TagEntry[] = [
    {
        name: "if / elsif / else",
        syntax: "{% if condition %}\n  …\n{% elsif condition %}\n  …\n{% else %}\n  …\n{% endif %}",
        descriptionKey: "liquidVariables.reference.tags.if",
    },
    {
        name: "unless",
        syntax: "{% unless condition %}\n  …\n{% endunless %}",
        descriptionKey: "liquidVariables.reference.tags.unless",
    },
    {
        name: "for",
        syntax: "{% for item in array %}\n  {{ item }}\n{% endfor %}",
        descriptionKey: "liquidVariables.reference.tags.for",
    },
    {
        name: "assign",
        syntax: '{% assign name = "value" %}',
        descriptionKey: "liquidVariables.reference.tags.assign",
    },
    {
        name: "capture",
        syntax: "{% capture name %}\n  …\n{% endcapture %}",
        descriptionKey: "liquidVariables.reference.tags.capture",
    },
    {
        name: "comment",
        syntax: "{% comment %}\n  …\n{% endcomment %}",
        descriptionKey: "liquidVariables.reference.tags.comment",
    },
];

const LIQUID_DOCS_URL = "https://shopify.github.io/liquid/basics/introduction/";

const LiquidVariablesModal: React.FC<LiquidVariablesModalProps> = ({ show, onClose, liquidVariables }) => {
    const { t } = useTranslation(["wizard", "common"]);
    const [showCopiedToast, setShowCopiedToast] = React.useState(false);

    return (
        <>
            <Modal show={show} onHide={onClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("liquidVariables.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs defaultActiveKey="variables" className="mb-3">
                        <Tab eventKey="variables" title={t("liquidVariables.tabVariables")}>
                            {liquidVariables != null && (
                                <table className="table table-striped table-align-middle">
                                    <thead>
                                        <tr>
                                            <th scope="col">{t("liquidVariables.nameHeader")}</th>
                                            <th scope="col">{t("liquidVariables.tagHeader")}</th>
                                            <th scope="col">{t("liquidVariables.exampleHeader")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {liquidVariables.map((liquidVariable) => {
                                            return (
                                                <tr
                                                    key={liquidVariable.id}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`{{${liquidVariable.id}}}`);
                                                        setShowCopiedToast(true);
                                                    }}
                                                >
                                                    <td>{liquidVariable.name}</td>
                                                    <td>
                                                        <i>&#123;&#123;{liquidVariable.id}&#125;&#125;</i>
                                                    </td>
                                                    <td>{liquidVariable.example}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </Tab>

                        <Tab eventKey="reference" title={t("liquidVariables.tabReference")}>
                            <div className="mb-4">
                                <Button
                                    variant="outline-primary"
                                    href={LIQUID_DOCS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    as="a"
                                >
                                    {t("liquidVariables.reference.docsLinkLabel")}
                                    <ExternalLink size={16} />
                                </Button>
                            </div>

                            <h6 className="fw-semibold">{t("liquidVariables.reference.customFiltersTitle")}</h6>
                            <p className="text-muted small">
                                {t("liquidVariables.reference.customFiltersDescription")}
                            </p>
                            <table className="table table-sm table-bordered mb-4">
                                <thead className="table-light">
                                    <tr>
                                        <th>{t("liquidVariables.reference.filterNameHeader")}</th>
                                        <th>{t("liquidVariables.reference.filterSyntaxHeader")}</th>
                                        <th>{t("liquidVariables.reference.filterDescriptionHeader")}</th>
                                        <th>{t("liquidVariables.reference.filterExampleHeader")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {CUSTOM_FILTERS.flatMap((f) =>
                                        f.variants.map((v, i) => (
                                            <tr key={`${f.name}-${i}`}>
                                                {i === 0 && (
                                                    <td rowSpan={f.variants.length} style={{ verticalAlign: "middle" }}>
                                                        <code>{f.name}</code>
                                                    </td>
                                                )}
                                                <td>
                                                    <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                                                        {v.syntax}
                                                    </pre>
                                                </td>
                                                <td className="small">{t(v.descriptionKey)}</td>
                                                <td>
                                                    <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                                                        {v.example}
                                                    </pre>
                                                </td>
                                            </tr>
                                        )),
                                    )}
                                </tbody>
                            </table>

                            <h6 className="fw-semibold">{t("liquidVariables.reference.builtinFiltersTitle")}</h6>
                            <p className="text-muted small">
                                {t("liquidVariables.reference.builtinFiltersDescription")}
                            </p>
                            <table className="table table-sm table-bordered mb-4">
                                <thead className="table-light">
                                    <tr>
                                        <th>{t("liquidVariables.reference.filterNameHeader")}</th>
                                        <th>{t("liquidVariables.reference.filterSyntaxHeader")}</th>
                                        <th>{t("liquidVariables.reference.filterDescriptionHeader")}</th>
                                        <th>{t("liquidVariables.reference.filterExampleHeader")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {BUILTIN_FILTERS.flatMap((f) =>
                                        f.variants.map((v, i) => (
                                            <tr key={`${f.name}-${i}`}>
                                                {i === 0 && (
                                                    <td rowSpan={f.variants.length} style={{ verticalAlign: "middle" }}>
                                                        <code>{f.name}</code>
                                                    </td>
                                                )}
                                                <td>
                                                    <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                                                        {v.syntax}
                                                    </pre>
                                                </td>
                                                <td className="small">{t(v.descriptionKey)}</td>
                                                <td>
                                                    <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                                                        {v.example}
                                                    </pre>
                                                </td>
                                            </tr>
                                        )),
                                    )}
                                </tbody>
                            </table>

                            <h6 className="fw-semibold">{t("liquidVariables.reference.tagsTitle")}</h6>
                            <p className="text-muted small">{t("liquidVariables.reference.tagsDescription")}</p>
                            <table className="table table-sm table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>{t("liquidVariables.reference.tagNameHeader")}</th>
                                        <th>{t("liquidVariables.reference.tagSyntaxHeader")}</th>
                                        <th>{t("liquidVariables.reference.tagDescriptionHeader")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {TAGS.map((tag) => (
                                        <tr key={tag.name}>
                                            <td>
                                                <code>{tag.name}</code>
                                            </td>
                                            <td>
                                                <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                                                    {tag.syntax}
                                                </pre>
                                            </td>
                                            <td className="small">{t(tag.descriptionKey)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>
                        {t("common:buttons.close")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <ToastContainer position="bottom-center" className="p-3">
                <Toast
                    bg="success"
                    show={showCopiedToast}
                    delay={1500}
                    autohide
                    onClose={() => setShowCopiedToast(false)}
                >
                    <Toast.Body>{t("liquidVariables.copiedToClipboard")}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default LiquidVariablesModal;
